import {
  useMutation,
  useQueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import { toast } from "@/components/toast/CustomToast";
import httpClient from "@/config/axios";
import type { NurseResponse } from "@/types/nurse-type";
import type { NurseDetailResponse } from "@/hooks/useNurseDetail";
import { useNurseAsOfMonthStore } from "@/store/nurseAsOfMonthStore";
import { invalidateNurseCaches } from "@/services/nurse/nurseCacheInvalidation";

interface PatchNurseRowVariables {
  nurseId: string;
  nurseName: string;
  patchBody: Record<string, unknown>;
  optimisticNurse: NurseResponse;
  listGroupId?: string | null;
  memberGroupId?: string | null;
  fullSchemaGroupId?: string | null;
}

interface CacheSnapshot<T> {
  key: QueryKey;
  data: T | undefined;
}

interface PatchNurseRowContext {
  listSnapshots: CacheSnapshot<NurseResponse[]>[];
  fullSchemaSnapshot: CacheSnapshot<NurseResponse[]>;
  detailSnapshot: CacheSnapshot<NurseDetailResponse>;
}

function patchNurseList(
  previous: NurseResponse[] | undefined,
  optimisticNurse: NurseResponse
): NurseResponse[] | undefined {
  if (!previous) return previous;

  let changed = false;
  const next = previous.map((nurse) => {
    if (nurse.nurse_id !== optimisticNurse.nurse_id) return nurse;
    changed = true;
    return {
      ...nurse,
      ...optimisticNurse,
    };
  });

  return changed ? next : previous;
}

function patchNurseDetail(
  previous: NurseDetailResponse | undefined,
  patchBody: Record<string, unknown>
): NurseDetailResponse | undefined {
  if (!previous) return previous;
  return {
    ...previous,
    ...patchBody,
  } as NurseDetailResponse;
}

export function usePatchNurseRow() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, PatchNurseRowVariables, PatchNurseRowContext>(
    {
      mutationFn: async ({ nurseId, patchBody, listGroupId }) => {
        // 월 셀렉터 선택월 → period valid_from(그 달부터 발효). 미설정 시 백엔드 today 폴백.
        const { year, month } = useNurseAsOfMonthStore.getState();
        const params = new URLSearchParams();
        if (listGroupId) params.set("group_id", listGroupId);
        if (year != null && month != null) {
          params.set("year", String(year));
          params.set("month", String(month));
        }
        const queryString = params.toString();
        await httpClient.patch(
          `/nurses/${nurseId}${queryString ? `?${queryString}` : ""}`,
          patchBody
        );
      },
      onMutate: async ({
        nurseId,
        patchBody,
        optimisticNurse,
        listGroupId,
        fullSchemaGroupId,
      }) => {
        const listScopeKey = QUERY_KEYS.nurses.listScope(listGroupId);
        const detailKey = QUERY_KEYS.nurses.detail(nurseId);
        const fullSchemaKey = [
          "nurses",
          "fullSchema",
          fullSchemaGroupId,
        ] as const;

        await Promise.all([
          queryClient.cancelQueries({ queryKey: listScopeKey }),
          queryClient.cancelQueries({ queryKey: detailKey }),
          queryClient.cancelQueries({ queryKey: fullSchemaKey }),
        ]);

        const listSnapshots = queryClient
          .getQueriesData<NurseResponse[]>({ queryKey: listScopeKey })
          .map(([key, data]) => ({ key, data }));
        const fullSchemaSnapshot = {
          key: fullSchemaKey,
          data: queryClient.getQueryData<NurseResponse[]>(fullSchemaKey),
        };
        const detailSnapshot = {
          key: detailKey,
          data: queryClient.getQueryData<NurseDetailResponse>(detailKey),
        };

        queryClient.setQueriesData<NurseResponse[] | undefined>(
          { queryKey: listScopeKey },
          (previous) => patchNurseList(previous, optimisticNurse)
        );
        queryClient.setQueryData<NurseResponse[] | undefined>(
          fullSchemaKey,
          (previous) => patchNurseList(previous, optimisticNurse)
        );
        queryClient.setQueryData<NurseDetailResponse | undefined>(
          detailKey,
          (previous) => patchNurseDetail(previous, patchBody)
        );

        return {
          listSnapshots,
          fullSchemaSnapshot,
          detailSnapshot,
        };
      },
      onError: (error, _variables, context) => {
        context?.listSnapshots.forEach(({ key, data }) => {
          queryClient.setQueryData(key, data);
        });
        if (context?.fullSchemaSnapshot) {
          queryClient.setQueryData(
            context.fullSchemaSnapshot.key,
            context.fullSchemaSnapshot.data
          );
        }
        if (context?.detailSnapshot) {
          queryClient.setQueryData(
            context.detailSnapshot.key,
            context.detailSnapshot.data
          );
        }
        toast.error(error.message || "근무자 정보 저장에 실패했습니다.");
      },
      onSuccess: (_data, variables) => {
        if (variables.nurseName) {
          toast.success(
            `${variables.nurseName} 간호사의 정보가 변경되었습니다.`
          );
          return;
        }
        toast.success("변경사항이 적용되었습니다.");
      },
      onSettled: async (_data, _error, variables) => {
        if (!variables) return;
        await invalidateNurseCaches(queryClient, {
          listGroupId: variables.listGroupId,
          memberGroupId: variables.memberGroupId,
          nurseId: variables.nurseId,
          fullSchemaGroupId: variables.fullSchemaGroupId,
        });
      },
    }
  );
}
